import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getSubscriptionTransactionsAPI, SubscriptionItem, SubscriptionTransaction } from '@src/api/subscription';
import { Dialog } from '@src/components/dialog';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { paymentOption } from '@src/utils/filter-helper';

const PLAN_COLORS: Record<string, { bgcolor: string; color: string }> = {
  DAY: { bgcolor: '#FFF3E0', color: '#E65100' },
  'Weekly VIP': { bgcolor: '#EDE8FF', color: '#6A00AC' },
  'Monthly VIP': { bgcolor: '#E6F9F0', color: '#00875A' },
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  COMPLETED: '#00875A',
  FAILED: '#DE350B',
  PENDING: '#D97706',
};

type Props = {
  open: boolean;
  onClose: () => void;
  item: SubscriptionItem | null;
};

const TransactionHistoryModal = ({ open, onClose, item }: Props) => {
  const { data, isLoading } = useQuery({
    queryKey: ['subscription-transactions', item?.id],
    queryFn: () => getSubscriptionTransactionsAPI({
      reference_id: item!.id, 
      purchase_item_type: "SUBSCRIPTION"
    }),
    enabled: open && !!item?.id,
  });

  if (!item) return null;

  const planLabel = item?.subscription?.name === 'Weekly VIP' ? 'Weekly' : item?.subscription?.name === 'DAY' ? 'Daily' : 'Monthly';
  const planStyle = PLAN_COLORS[item?.subscription?.name] || { bgcolor: '#F3F4F6', color: '#6B7280' };
  const statusColor = item.status === 'ACTIVE' ? '#00875A' : '#DE350B';
  const transactions = data?.items ?? [];

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      maxWidth="md"
      dividers={false}
      dialogTitle={
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontSize={14} fontWeight={700} letterSpacing={1} color="#111827">
            TRANSACTION HISTORY
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
      dialogContent={
        <Box sx={{ width: '100%' }}>
          {/* User + plan summary */}
          <Stack direction="row" alignItems="stretch" mb={2.5} gap={0}>
            {/* User info */}
            <Box sx={{ pr: 3 }}>
              <Typography fontSize={15} fontWeight={700} color="#111827">
                {item.user?.displayName ?? 'Unknown'}
              </Typography>
              <Typography fontSize={12} color="#6B7280">
                USER ID: {item.user?.id}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

            {/* Current Plan */}
            <Box sx={{ px: 3 }}>
              <Typography fontSize={10} fontWeight={600} color="#9CA3AF" textTransform="uppercase" letterSpacing={0.8} mb={0.5}>
                Current Plan
              </Typography>
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography fontSize={13} fontWeight={600} color="#111827">
                  {item.subscription?.name}
                </Typography>
                <Chip
                  label={planLabel}
                  size="small"
                  sx={{ bgcolor: planStyle.bgcolor, color: planStyle.color, fontWeight: 700, fontSize: 11, borderRadius: '20px' }}
                />
              </Stack>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

            {/* Status */}
            <Box sx={{ px: 3 }}>
              <Typography fontSize={10} fontWeight={600} color="#9CA3AF" textTransform="uppercase" letterSpacing={0.8} mb={0.5}>
                Status
              </Typography>
              <Typography fontSize={13} fontWeight={700} color={statusColor}>
                {item.status}
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Table */}
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Display Name', 'Transaction ID', 'Price', 'Payment Method', "Action", 'Payment Status', 'Transaction Date'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{ fontSize: 12, fontWeight: 700, color: '#2D60FF', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#9CA3AF', fontSize: 13 }}>
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((tx: SubscriptionTransaction) => {
                      const txStatusColor = PAYMENT_STATUS_COLORS[tx.status] || '#6B7280';

                      return (
                        <TableRow key={tx.id} hover>
                          <TableCell sx={{ fontSize: 13, color: '#111827' }}>
                            {tx.user?.displayName ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: '#111827' }}>
                            {tx.transactionNo ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: '#111827' }}>
                            €{tx.subscription?.price ?? tx.amount}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: '#111827' }}>
                            {paymentOption(tx.paymentTypeID) ?? '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: '#111827' }}>
                            {tx.action ?? '-'}
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={600} color={txStatusColor}>
                              {tx.status ?? '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, color: '#111827', whiteSpace: 'nowrap' }}>
                            {tx.transactionDate ? moment.parseZone(tx.transactionDate).format('MM-DD-YYYY hh:mm A') : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          )}

          <Stack alignItems="center" mt={3}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              sx={{ borderRadius: '8px', height: 40, fontWeight: 700, fontSize: 13, minWidth: 160 }}
            >
              CLOSE
            </Button>
          </Stack>
        </Box>
      }
    />
  );
};

export default TransactionHistoryModal;
